import { observable, action, toJS, computed, runInAction, autorun } from 'mobx';
import { IAuthenticateStoreImpl, IAuthenticateData, IAccessToken } from './interface';
import { tokenAuth, refreshTokenAuth, ILanguage, deletePlayerId } from 'src/modules/api';
import { task } from 'mobx-task';
import * as jwt from 'jsonwebtoken';
import * as Cookies from 'js-cookie';
import { axiosInstance } from 'src/modules/api';
import { updateConfiguration } from 'src/modules/appConfiguration';
import RootStore from '../rootStore';
import { isNil } from 'lodash';
import { Router, Redirect } from 'react-router';

export class AuthenticateStore implements IAuthenticateStoreImpl {
  public rootStore: RootStore;
  @observable public authenticated: boolean = false;
  @observable public authenticating: boolean = false;
  @observable public accessTokenData: string;
  
  public signIn = task(async (userNameOrEmailAddress:string, password:string) => {
    try {
      const response = await tokenAuth(userNameOrEmailAddress, password);
      const { data: { result } } = response;
      const { accessToken, refreshToken } = result as IAuthenticateData;
      this.accessTokenData = accessToken;
      
      // tslint:disable-next-line: no-string-literal
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      const { exp: refreshTokenExp } = jwt.decode(refreshToken) as any;
      
      Cookies.set('refreshToken', refreshToken, {
        expires: new Date(refreshTokenExp * 1000),
      });
      
      this.loginSuccessAction(accessToken);
      return response;
    } catch (e) {
      return e.response;
    }
  });

  public refreshAccessToken = task(async () => {
    const refreshToken = Cookies.get('refreshToken');
    if (refreshToken === undefined) {
      // TODO: redirect to login
      // window.location.replace('/login');
      this.signOut();
    }
    
    refreshTokenAuth(refreshToken!).then((response: any) => {
      const { data: { result } } = response;
      const accessToken = result.accessToken as string;
      this.loginSuccessAction(accessToken);
    }).catch( error => {
      // TODO: redirect to login
      // window.location.replace('/login');
      this.signOut();
    });
  })

  public signOut = task(async () => {
    try {
      await deletePlayerId();
    } catch (e) {
      //
    } finally {
      Cookies.remove('refreshToken');
      Cookies.remove('accessToken');
      this.authenticated = false;
    }
  });

  @observable private roles$: string[] = [];
  @observable private timeZone$: string;
  @observable private currentLanguage$: ILanguage;
  @observable private languages$: ILanguage[];
  
  
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const accessToken = Cookies.get('accessToken');

    if (accessToken === undefined) {
      this.refreshAccessToken();
    } else {
      this.loginSuccessAction(accessToken);
    }
  }

  

  @action
  private loginSuccessAction(accessToken: string) {
    // tslint:disable-next-line: no-string-literal
    this.updateAccessToken(accessToken);
  }
  
  private updateAccessToken = (accessToken: string) => {
    const { exp: accessTokenTokenExp } = jwt.decode(accessToken) as any;
    // tslint:disable-next-line: no-string-literal
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    Cookies.set('accessToken', accessToken, {
      expires: new Date(accessTokenTokenExp * 1000),
    });

    const decodeByAccessToken = jwt.decode(accessToken) as IAccessToken;

    // Role 정보 저장
    runInAction(() => {
      const role = decodeByAccessToken.role;
      this.roles$ = Array.isArray(role) ? role : (role ? [role] : []);
    });
    
    updateConfiguration()
    .then(({ timeZone, currentLanguage, languages }) => {
      runInAction(() => {
        this.timeZone$ = timeZone;
        this.currentLanguage$ = currentLanguage;
        this.languages$ = languages;
      });
    })

    runInAction(() => {
      this.rootStore.userStore.fetchUserAsync();
      this.authenticated = true;
      this.rootStore.userStore.setUserId = decodeByAccessToken.sub;
    });
  };

  @computed
  get roles() {
    return toJS(this.roles$);
  }

  @computed
  get isFacilityAdmin() {
    return this.roles$.includes('Facility Admin') || this.roles$.includes('Admin') || this.roles$.includes('Manager');
  }

  @computed
  get isFacilityAdminOnly() {
    return this.roles$.includes('Facility Admin') && !this.roles$.includes('Admin') && !this.roles$.includes('Manager');
  }

  @computed get timeZone() {
    const timeZone = toJS(this.timeZone$);
    if (isNil(timeZone)) {
      return '';
    };
    return timeZone;
  }

  @computed get currentLanguage(): Partial<ILanguage> {
    const currentLanguage = toJS(this.currentLanguage$);
    if (isNil(currentLanguage)) {
      return {};
    }
    return toJS(this.currentLanguage$);
  }

  @computed get languages() {
    const languages = toJS(this.languages$);
    if (isNil(languages)) {
      return [];
    }
    return languages;
  }

  @computed
  get accessToken() {
    return toJS(this.accessTokenData);
  }
  @computed
  get refreshToken() {
    return Cookies.get('refreshToken');
  }

  @computed
  get isAuthenticated() {
    return this.authenticated;
  }
};



// WEBPACK FOOTER //
// ./src/stores/authenticate/index.ts